import { useState, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import DocumentFileUpload from '../components/DocumentFileUpload';
import { PiIdentificationBadgeFill } from 'react-icons/pi';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

import { PessoaType } from '../../db/Orm';
import { ProgressSpinner } from 'primereact/progressspinner';
import API from '../../api/API';

type ResponsavelTecnicoProps = {
    visible: boolean;
    setVisible: (visible: boolean) => void;
    pessoas: PessoaType[];
    onSuccess?: () => void;
};

export default function ResponsavelTecnico(props: ResponsavelTecnicoProps) {
    const toast = useRef<Toast>(null);
    const [registroConselho, setRegistroConselho] = useState<string>('');
    const [conselho, setConselho] = useState<string | null>(null);
    const [ufConselho, setUfConselho] = useState<string | null>(null);
    const [carteiraHabilitacao, setCarteiraHabilitacao] = useState<File | null>(null);
    const [selectedPessoa, setSelectedPessoa] = useState<PessoaType | null>(null);
    const [loadingSave, setLoadingSave] = useState<boolean>(false);

    const conselhoOptions = [
        { label: 'CREA', value: 'CREA' },
        { label: 'CFT', value: 'CFT' },
    ];

    const ufOptions = [
        { label: 'AC', value: 'AC' }, { label: 'AL', value: 'AL' }, { label: 'AP', value: 'AP' },
        { label: 'AM', value: 'AM' }, { label: 'BA', value: 'BA' }, { label: 'CE', value: 'CE' },
        { label: 'DF', value: 'DF' }, { label: 'ES', value: 'ES' }, { label: 'GO', value: 'GO' },
        { label: 'MA', value: 'MA' }, { label: 'MT', value: 'MT' }, { label: 'MS', value: 'MS' },
        { label: 'MG', value: 'MG' }, { label: 'PA', value: 'PA' }, { label: 'PB', value: 'PB' },
        { label: 'PR', value: 'PR' }, { label: 'PE', value: 'PE' }, { label: 'PI', value: 'PI' },
        { label: 'RJ', value: 'RJ' }, { label: 'RN', value: 'RN' }, { label: 'RS', value: 'RS' },
        { label: 'RO', value: 'RO' }, { label: 'RR', value: 'RR' }, { label: 'SC', value: 'SC' },
        { label: 'SP', value: 'SP' }, { label: 'SE', value: 'SE' }, { label: 'TO', value: 'TO' },
    ];

    const handleSubmit = async () => {
        //console.log({selectedPessoa, registroConselho, conselho, ufConselho, carteiraHabilitacao})
        const errors = [];
        if (!selectedPessoa) errors.push('Pessoa');
        if (!registroConselho) errors.push('Número do Registro no Conselho');
        if (!conselho) errors.push('Conselho');
        if (!ufConselho) errors.push('UF do Conselho');
        if (!carteiraHabilitacao) errors.push('Carteira de Habilitação');

        if (errors.length > 0) {
            toast.current?.show({
                severity: 'error',
                summary: 'Campos obrigatórios',
                detail: `Os seguintes campos devem ser preenchidos: ${errors.join(', ')}.`,
                life: 4000,
            });
            return;
        }
        if (!(selectedPessoa && registroConselho && conselho && ufConselho && carteiraHabilitacao)) return
        setLoadingSave(true);
        const response = await API.cadastrarRT(selectedPessoa, registroConselho, conselho, ufConselho, carteiraHabilitacao)
        if (response instanceof Error){
            toast.current?.show({
                severity: 'error',
                summary: 'Erro ao salvar',
                detail: `Ocorreu um erro ao salvar o Responsável Técnico: ${response.message}`,
                life: 4000,
            });
        } else {
            toast.current?.show({
                severity: 'success',
                summary: 'Sucesso',
                detail: 'Responsável Técnico cadastrado com sucesso!',
                life: 3000,
            });
            props.onSuccess && props.onSuccess();
            setTimeout(() => props.setVisible(false), 1500);
        }
        setLoadingSave(false)
    };

    const headerElement = (
        <div style={{ display: 'flex', alignItems: 'center' }}>
            <PiIdentificationBadgeFill style={{ marginRight: 5 }} />
            <span>Responsável Técnico</span>
        </div>
    );

    const footerElement = (
        <div>
            <Button label={loadingSave ? '' : "Salvar"} icon={loadingSave ? '' : "pi pi-check"} onClick={handleSubmit} disabled={loadingSave} >{loadingSave && <ProgressSpinner style={{ width: '20px', height: '20px' }} strokeWidth="8" />}</Button>
            <Button label="Cancelar" icon="pi pi-times" onClick={() => props.setVisible(false)} className="p-button-text" />
        </div>
    );

    return (
        <Dialog
            visible={props.visible}
            header={headerElement}
            footer={footerElement}
            style={{ width: '500px' }}
            onHide={() => props.setVisible(false)}
            modal
        >
            <Toast ref={toast} />
            <div className="p-fluid">
                <div className="p-field" style={{ marginBottom: '1rem' }}>
                    <span className="p-float-label">
                        <Dropdown
                            id="pessoa"
                            value={selectedPessoa}
                            options={props.pessoas}
                            onChange={(e) => setSelectedPessoa(e.value)}
                            optionLabel="nome_razao"
                            placeholder="Selecione a Pessoa"
                        />
                        <label htmlFor="pessoa">Pessoa</label>
                    </span>
                </div>

                <div className="p-field" style={{ marginBottom: '1rem' }}>
                    <span className="p-float-label">
                        <InputText
                            id="registroConselho"
                            value={registroConselho}
                            onChange={(e) => setRegistroConselho(e.target.value)}
                        />
                        <label htmlFor="registroConselho">Número do Registro no Conselho</label>
                    </span>
                </div>

                <div className="p-field" style={{ marginBottom: '1rem' }}>
                    <span className="p-float-label">
                        <Dropdown
                            id="conselho"
                            value={conselho}
                            options={conselhoOptions}
                            onChange={(e) => setConselho(e.value)}
                            placeholder="Selecione o Conselho"
                        />
                        <label htmlFor="conselho">Conselho</label>
                    </span>
                </div>

                <div className="p-field" style={{ marginBottom: '1rem' }}>
                    <span className="p-float-label">
                        <Dropdown
                            id="ufConselho"
                            value={ufConselho}
                            options={ufOptions}
                            onChange={(e) => setUfConselho(e.value)}
                            placeholder="Selecione a UF do Conselho"
                        />
                        <label htmlFor="ufConselho">UF do Conselho</label>
                    </span>
                </div>

                <DocumentFileUpload
                    label="Carteira de Habilitação ao Órgão Competente"
                    icon={<InsertDriveFileIcon />}
                    onFileSelect={(file) => setCarteiraHabilitacao(file)}
                    editMode={true}
                    accept='application/pdf'
                />
            </div>
        </Dialog>
    );
}
